import React, { useState, useEffect } from 'react';
import { 
  Download, FileArchive, FileSpreadsheet, FileJson, 
  AlertTriangle, CheckCircle, Clock, HardDrive,
  Users, Calendar, CreditCard, FileText, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExportDati() {
  const [anteprima, setAnteprima] = useState(null);
  const [statoAbbonamento, setStatoAbbonamento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const [anteprimaRes, statoRes] = await Promise.all([
        fetch(`${API_URL}/api/export/anteprima`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/export/stato-abbonamento`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (anteprimaRes.ok) {
        setAnteprima(await anteprimaRes.json());
      }
      if (statoRes.ok) {
        setStatoAbbonamento(await statoRes.json());
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async (collection) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/export/csv/${collection}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${collection}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${collection}.csv scaricato`);
      } else {
        toast.error('Errore nel download');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleExportZip = async () => {
    setExporting(true);
    toast.info('Preparazione archivio in corso... Potrebbe richiedere alcuni minuti.');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/export/zip-completo`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `export_completo_${new Date().toISOString().split('T')[0]}.zip`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Archivio ZIP scaricato con successo!');
      } else {
        toast.error('Errore nella generazione dell\'archivio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setExporting(false);
    }
  };

  const getStatoIcon = (stato) => {
    switch (stato) {
      case 'attivo': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'in_scadenza': return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'grace_period': return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      case 'scaduto': return <AlertTriangle className="h-5 w-5 text-red-500" />;
      default: return <CheckCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatoColor = (stato) => {
    switch (stato) {
      case 'attivo': return 'bg-green-50 border-green-200 text-green-800';
      case 'in_scadenza': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'grace_period': return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'scaduto': return 'bg-red-50 border-red-200 text-red-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const csvExports = [
    { id: 'volontari', label: 'Volontari', icon: Users },
    { id: 'persone', label: 'Persone/Soci', icon: Users },
    { id: 'eventi', label: 'Eventi', icon: Calendar },
    { id: 'presenze', label: 'Presenze', icon: Clock },
    { id: 'quote_associative', label: 'Quote Associative', icon: CreditCard },
    { id: 'entrate', label: 'Entrate', icon: FileText },
    { id: 'uscite', label: 'Uscite', icon: FileText },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="export-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Download className="h-6 w-6" />
          Esporta i Tuoi Dati
        </h1>
        <p className="text-gray-500 mt-1">
          Scarica una copia completa di tutti i dati della tua organizzazione
        </p>
      </div>

      {/* Stato Abbonamento */}
      {statoAbbonamento && (
        <Alert className={getStatoColor(statoAbbonamento.stato)}>
          <div className="flex items-start gap-3">
            {getStatoIcon(statoAbbonamento.stato)}
            <div>
              <AlertTitle className="font-semibold">
                Stato Abbonamento: {statoAbbonamento.stato.replace('_', ' ').toUpperCase()}
              </AlertTitle>
              <AlertDescription className="mt-1">
                {statoAbbonamento.messaggio}
                {statoAbbonamento.giorni_rimanenti > 0 && statoAbbonamento.stato !== 'attivo' && (
                  <span className="block mt-1 font-medium">
                    Giorni rimanenti: {statoAbbonamento.giorni_rimanenti}
                  </span>
                )}
              </AlertDescription>
            </div>
          </div>
        </Alert>
      )}

      {/* Anteprima Dati */}
      {anteprima && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Anagrafiche</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span>Volontari</span>
                  <span className="font-bold">{anteprima.contenuto?.anagrafiche?.volontari || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Persone/Soci</span>
                  <span className="font-bold">{anteprima.contenuto?.anagrafiche?.persone || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Ruoli</span>
                  <span className="font-bold">{anteprima.contenuto?.anagrafiche?.ruoli_assegnati || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Attività</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span>Eventi</span>
                  <span className="font-bold">{anteprima.contenuto?.attivita?.eventi || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Presenze</span>
                  <span className="font-bold">{anteprima.contenuto?.attivita?.presenze || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Documenti</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span>Doc. Ente</span>
                  <span className="font-bold">{anteprima.contenuto?.documenti?.documenti_ente || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Doc. Volontari</span>
                  <span className="font-bold">{anteprima.contenuto?.documenti?.documenti_volontari || 0}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Dimensione</span>
                  <span>{anteprima.contenuto?.documenti?.dimensione_stimata_mb || 0} MB</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Contabilità</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span>Entrate</span>
                  <span className="font-bold">{anteprima.contenuto?.contabilita?.entrate || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Uscite</span>
                  <span className="font-bold">{anteprima.contenuto?.contabilita?.uscite || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Quote</span>
                  <span className="font-bold">{anteprima.contenuto?.contabilita?.quote_associative || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Export Completo */}
      <Card className="border-2 border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileArchive className="h-5 w-5 text-blue-600" />
            Export Completo (ZIP)
          </CardTitle>
          <CardDescription>
            Scarica un unico archivio ZIP contenente tutti i dati e i documenti caricati
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              <p>L'archivio includerà:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Tutte le anagrafiche in formato CSV</li>
                <li>Tutti i documenti caricati (organizzati in cartelle)</li>
                <li>File indice con riepilogo contenuti</li>
              </ul>
            </div>
            <Button 
              size="lg" 
              onClick={handleExportZip}
              disabled={exporting}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="export-zip-btn"
            >
              {exporting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Preparazione...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Scarica Archivio Completo
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Export Singoli CSV */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-green-600" />
            Export Singoli (CSV)
          </CardTitle>
          <CardDescription>
            Scarica singole tabelle in formato CSV per Excel/LibreOffice
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {csvExports.map((item) => (
              <Button
                key={item.id}
                variant="outline"
                onClick={() => handleExportCSV(item.id)}
                className="justify-start"
                data-testid={`export-csv-${item.id}`}
              >
                <item.icon className="h-4 w-4 mr-2 text-gray-500" />
                {item.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Note */}
      <Card className="bg-gray-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <HardDrive className="h-5 w-5 text-gray-400 mt-0.5" />
            <div className="text-sm text-gray-600">
              <p className="font-medium">Informazioni sull'export:</p>
              <ul className="mt-2 space-y-1">
                <li>• I file CSV sono in formato UTF-8, compatibili con Excel e LibreOffice</li>
                <li>• I documenti nell'archivio ZIP mantengono il nome e formato originale</li>
                <li>• L'export è sempre disponibile, anche in caso di abbonamento scaduto</li>
                <li>• Ti consigliamo di effettuare backup periodici dei tuoi dati</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
